/* Incentive Bundles — client-side logic */
(function () {
  'use strict';

  // ── State ───────────────────────────────────────────────────────────────────
  var allBundles      = [];
  var filteredBundles = [];
  var selectedPublisher = '';   // empty string = All; single-select
  var searchQuery = '';
  var sortOrder   = 'az';

  // Cart state — variantId (string) → quantity currently in cart
  var cartQuantities = {};

  // Accordion state — only one bundle expanded at a time
  var expandedRow    = null;   // currently expanded DOM row element
  var expandedBundle = null;   // bundle data for expanded row

  // Product data cache (handle → full product JSON)
  var productCache = {};

  // IntersectionObserver for viewport-triggered pre-fetching
  var prefetchObserver = null;

  // ── Element refs ────────────────────────────────────────────────────────────
  var el = {};

  // ── Init ────────────────────────────────────────────────────────────────────
  function init() {
    el.bundles    = document.getElementById('ib-bundles');
    el.loading    = document.getElementById('ib-loading');
    el.error      = document.getElementById('ib-error');
    el.empty      = document.getElementById('ib-empty');
    el.count      = document.getElementById('ib-count');
    el.search     = document.getElementById('ib-search');
    el.sort       = document.getElementById('ib-sort');
    el.pubPills   = document.getElementById('ib-publisher-pills');
    el.pubSelect  = document.getElementById('ib-publisher-select');

    el.pubSelect.addEventListener('change', function () {
      selectedPublisher = this.value === 'all' ? '' : this.value;
      updatePublisherUI();
      applyFilters();
    });

    el.search.addEventListener('input', function () {
      searchQuery = el.search.value.trim().toLowerCase();
      applyFilters();
    });
    el.sort.addEventListener('change', function () {
      sortOrder = el.sort.value;
      applyFilters();
    });

    // IntersectionObserver: pre-fetch product data when a row enters the viewport
    if (typeof IntersectionObserver !== 'undefined') {
      prefetchObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var sid    = entry.target.dataset.bundleSid;
          var bundle = getBundleBySid(sid);
          if (bundle) {
            bundle.regulars.forEach(function (p) {
              fetchProductFull(p.handle).catch(function () {});
            });
          }
          prefetchObserver.unobserve(entry.target);
        });
      }, { rootMargin: '300px 0px' });
    }

    loadBundles();

    // Stay in sync with cart drawer updates (items removed or qty changed)
    if (typeof subscribe === 'function') {
      subscribe('cart-update', function () { onCartUpdate(); });
    }
  }

  function getBundleBySid(sid) {
    for (var i = 0; i < allBundles.length; i++) {
      if (allBundles[i].sid === sid) return allBundles[i];
    }
    return null;
  }

  // ── Data loading ─────────────────────────────────────────────────────────────
  async function loadBundles() {
    showState('loading');
    var proxyUrl = (window.IB && window.IB.proxyUrl) || '';
    if (!proxyUrl) {
      showState('error');
      el.error.textContent = 'Proxy URL not configured. Contact the store admin.';
      return;
    }

    try {
      var cartFetch = fetchJSON('/cart.js').catch(function () { return null; });
      var incentiveProducts = await fetchJSON(proxyUrl + '/incentive-products');
      var cartData = await cartFetch;
      cartQuantities = {};
      if (cartData && Array.isArray(cartData.items)) {
        cartData.items.forEach(function (item) {
          var vid = String(item.variant_id);
          cartQuantities[vid] = (cartQuantities[vid] || 0) + item.quantity;
        });
      }
      console.log('[IB] incentive products raw:', incentiveProducts);
      if (!Array.isArray(incentiveProducts) || !incentiveProducts.length) {
        showState('empty');
        return;
      }

      var incentiveHandles = {};
      incentiveProducts.forEach(function (p) { incentiveHandles[p.handle] = true; });

      var sidMap = {};
      incentiveProducts.forEach(function (p) {
        var sid = getSidTag(p.tags);
        console.log('[IB] product', p.handle, '→ sid:', sid, '| tags:', p.tags);
        if (!sid) return;
        if (!sidMap[sid]) sidMap[sid] = [];
        sidMap[sid].push(p);
      });

      var sidTags = Object.keys(sidMap);
      if (!sidTags.length) { showState('empty'); return; }

      var regularResults = await Promise.all(sidTags.map(function (sid) {
        return fetchRegularsBySid(sid, incentiveHandles);
      }));

      allBundles = sidTags.map(function (sid, i) {
        var incentives = sidMap[sid];
        var regulars   = regularResults[i] || [];
        return {
          sid:         sid,
          publisher:   incentives[0].vendor || '',
          seriesTitle: extractSeriesTitle(incentives[0].title),
          incentives:  incentives,
          regulars:    regulars,
          ratios:      extractRatios(incentives),
        };
      });

      allBundles = allBundles.filter(function (b) { return b.regulars.length > 0; });
      console.log('[IB] bundles ready:', allBundles.length);

      if (!allBundles.length) { showState('empty'); return; }

      buildPublisherPills();
      applyFilters();
      showState('bundles');
    } catch (err) {
      console.error('[IncentiveBundles] load error', err);
      showState('error');
    }
  }

  async function fetchRegularsBySid(sid, incentiveHandles) {
    try {
      var token = (window.IB && window.IB.storefrontToken) || '';
      if (!token) { console.error('[IB] storefrontToken not set'); return []; }

      var query = '{ products(first: 50, query: "tag:\\"' + sid + '\\"") { edges { node { handle title tags images(first:1) { edges { node { url } } } variants(first:1) { edges { node { id price { amount } compareAtPrice { amount } } } } } } } }';
      var r = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: query }),
      });
      if (!r.ok) throw new Error('Storefront API HTTP ' + r.status);
      var data = await r.json();
      var edges = (data.data && data.data.products && data.data.products.edges) || [];
      var products = edges.map(function (e) {
        var node    = e.node;
        var imgEdge = node.images && node.images.edges && node.images.edges[0];
        var varEdge = node.variants && node.variants.edges && node.variants.edges[0];
        return {
          handle:           node.handle,
          title:            node.title,
          tags:             node.tags || [],
          image:            imgEdge ? { url: imgEdge.node.url } : null,
          variantId:        varEdge ? varEdge.node.id.split('/').pop() : null,
          price:            varEdge ? varEdge.node.price.amount : null,
          compare_at_price: varEdge && varEdge.node.compareAtPrice ? varEdge.node.compareAtPrice.amount : null,
          variants:         varEdge ? [{ id: varEdge.node.id.split('/').pop(), price: varEdge.node.price.amount }] : [],
        };
      });
      products = products.filter(function (p) {
        var hasExactSid  = p.tags.some(function (t) { return t.trim() === sid; });
        var isIncentive  = p.tags.some(function (t) { return t.trim() === 'incentive'; });
        var hasPreorder  = p.tags.some(function (t) { return t.trim().toLowerCase() === 'preorder'; });
        // Only include active pre-orders — when FOC Closeout removes the preorder tag
        // this product drops out, and if all regulars drop out the bundle disappears too.
        return hasExactSid && !isIncentive && !incentiveHandles[p.handle] && hasPreorder;
      });
      return products;
    } catch (e) {
      console.error('[IB] fetchRegularsBySid error for', sid, e);
      return [];
    }
  }

  async function fetchJSON(url) {
    var r = await fetch(url);
    if (!r.ok) throw new Error('HTTP ' + r.status + ' fetching ' + url);
    return r.json();
  }

  async function fetchProductFull(handle) {
    if (productCache[handle]) return productCache[handle];
    var data = await fetchJSON('/products/' + handle + '.js');
    productCache[handle] = data;
    return data;
  }

  // ── Filters ──────────────────────────────────────────────────────────────────
  function buildPublisherPills() {
    var publishers = [];
    allBundles.forEach(function (b) {
      if (b.publisher && publishers.indexOf(b.publisher) === -1)
        publishers.push(b.publisher);
    });
    publishers.sort();
    publishers.forEach(function (pub) {
      // desktop pill
      var btn = document.createElement('button');
      btn.className = 'ib-pill';
      btn.dataset.publisher = pub;
      btn.textContent = pub;
      btn.addEventListener('click', onPublisherPill);
      el.pubPills.appendChild(btn);
      // mobile select option
      var opt = document.createElement('option');
      opt.value = pub;
      opt.textContent = pub;
      el.pubSelect.appendChild(opt);
    });
    el.pubPills.querySelector('[data-publisher="all"]')
      .addEventListener('click', onPublisherPill);
  }

  function onPublisherPill(e) {
    var val = e.currentTarget.dataset.publisher;
    // clicking the active publisher or "All" resets to All
    if (val === 'all' || val === selectedPublisher) {
      selectedPublisher = '';
    } else {
      selectedPublisher = val;
    }
    updatePublisherUI();
    applyFilters();
  }

  function updatePublisherUI() {
    el.pubPills.querySelectorAll('[data-publisher]').forEach(function (p) {
      p.classList.toggle('ib-pill--active',
        selectedPublisher === '' ? p.dataset.publisher === 'all' : p.dataset.publisher === selectedPublisher);
    });
    if (el.pubSelect) el.pubSelect.value = selectedPublisher || 'all';
  }

  function applyFilters() {
    filteredBundles = allBundles.filter(function (b) {
      if (selectedPublisher && b.publisher !== selectedPublisher) return false;
      if (searchQuery) {
        var hay = (b.seriesTitle + ' ' + b.publisher).toLowerCase();
        if (hay.indexOf(searchQuery) === -1) return false;
      }
      return true;
    });

    filteredBundles.sort(function (a, b) {
      var A = a.seriesTitle.toUpperCase(), B = b.seriesTitle.toUpperCase();
      return sortOrder === 'za' ? B.localeCompare(A) : A.localeCompare(B);
    });

    renderBundles();

    el.count.textContent = 'Showing ' + filteredBundles.length + ' of ' +
      allBundles.length + ' bundle' + (allBundles.length !== 1 ? 's' : '');
  }

  // ── Rendering ────────────────────────────────────────────────────────────────
  function renderBundles() {
    // Reset accordion state — re-render clears all expanded rows
    expandedRow    = null;
    expandedBundle = null;

    el.bundles.innerHTML = '';
    if (!filteredBundles.length) {
      el.empty.style.display = '';
      return;
    }
    el.empty.style.display = 'none';
    filteredBundles.forEach(function (bundle) {
      el.bundles.appendChild(buildBundleRow(bundle));
    });
  }

  function buildBundleRow(bundle) {
    var row = document.createElement('div');
    row.className = 'ib-bundle';
    row.dataset.bundleSid = bundle.sid;

    // Register with IntersectionObserver for viewport-triggered pre-fetching
    if (prefetchObserver) prefetchObserver.observe(row);

    // ── Header ──
    var hdr = document.createElement('div');
    hdr.className = 'ib-bundle__header';
    hdr.style.cursor = 'pointer';
    hdr.innerHTML =
      '<span class="ib-bundle__series">'    + esc(bundle.seriesTitle) + '</span>' +
      '<span class="ib-bundle__publisher">' + esc(bundle.publisher)   + '</span>';
    hdr.addEventListener('click', function () { toggleAccordion(bundle, row, btn); });

    // ── Body (compact preview) ──
    var body = document.createElement('div');
    body.className = 'ib-bundle__body';

    // Bottom: incentive covers + tagline + button
    var bottomSection = document.createElement('div');
    bottomSection.className = 'ib-bundle__bottom';

    var incGroup = document.createElement('div');
    incGroup.className = 'ib-bundle__inc-group';
    bundle.incentives.slice().sort(function (a, b) {
      return thresholdOf(getRatioFromTags(a.tags)) - thresholdOf(getRatioFromTags(b.tags));
    }).forEach(function (p) {
      var ratio   = getRatioFromTags(p.tags);
      var variant = p.variants && p.variants[0];
      var vidRaw  = variant ? String(variant.id) : '';
      var vidStr  = vidRaw.indexOf('/') !== -1 ? vidRaw.split('/').pop() : vidRaw;
      var wrap  = document.createElement('div');
      wrap.className = 'ib-bundle__incentive-wrap';
      wrap.dataset.threshold = thresholdOf(ratio);
      wrap.dataset.variantId = vidStr;

      // Ratio badge + title ABOVE artwork
      var badge = document.createElement('div');
      badge.className = 'ib-bundle__ratio-badge';
      badge.textContent = ratio || 'Incentive';
      var titleEl = document.createElement('div');
      titleEl.className   = 'ib-bundle__inc-cover-title';
      titleEl.textContent = p.title || '';

      // Image wrapped with overlay (overlay shown when drawer is open)
      var imgWrap = document.createElement('div');
      imgWrap.className = 'ib-bundle__inc-img-wrap';
      var img = document.createElement('img');
      img.className = 'ib-bundle__thumb';
      img.src       = shopifyImg(p.image, 360);
      img.alt       = p.title || '';
      img.loading   = 'lazy';
      img.width     = 180;
      img.height    = 270;
      var overlay = document.createElement('div');
      overlay.className = 'ib-bundle__inc-overlay';
      overlay.innerHTML =
        '<span class="ib-bundle__inc-lock-icon">🔒</span>' +
        '<span class="ib-bundle__inc-lock-label"></span>';
      imgWrap.appendChild(img);
      imgWrap.appendChild(overlay);

      wrap.appendChild(badge);
      wrap.appendChild(titleEl);
      wrap.appendChild(imgWrap);
      incGroup.appendChild(wrap);
    });

    var spacer = document.createElement('div');
    spacer.className = 'ib-bundle__spacer';

    var btnCol = document.createElement('div');
    btnCol.className = 'ib-bundle__btn-col';

    var tagline = document.createElement('p');
    tagline.className = 'ib-bundle__inc-tagline';
    tagline.innerHTML = 'Mix &amp; match covers to earn your FREE incentives';

    var btn = document.createElement('button');
    btn.className = 'ib-bundle__btn';
    btn.textContent = 'Select Covers';
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleAccordion(bundle, row, btn);
    });

    btnCol.appendChild(tagline);
    btnCol.appendChild(btn);
    bottomSection.appendChild(incGroup);
    bottomSection.appendChild(spacer);
    bottomSection.appendChild(btnCol);

    body.appendChild(bottomSection);

    row.appendChild(hdr);
    row.appendChild(body);
    return row;
  }

  // ── Accordion ────────────────────────────────────────────────────────────────
  function toggleAccordion(bundle, row, btn) {
    if (expandedRow === row) {
      collapseAccordion(row, btn);
    } else {
      // Collapse currently open row first
      if (expandedRow) {
        var oldBtn = expandedRow.querySelector('.ib-bundle__btn');
        collapseAccordion(expandedRow, oldBtn);
      }
      expandAccordion(bundle, row, btn);
    }
  }

  function expandAccordion(bundle, row, btn) {
    expandedRow    = row;
    expandedBundle = bundle;

    if (btn) btn.textContent = '▲ Close';
    row.classList.add('ib-bundle--expanded');

    var section = buildAccordionSection(bundle, row);
    row.appendChild(section);

    // Animate open: measure height after appending to DOM, then transition
    section.getBoundingClientRect(); // force reflow
    section.style.maxHeight = section.scrollHeight + 'px';

    section.addEventListener('transitionend', function handler() {
      section.style.maxHeight = 'none'; // allow content to grow freely
      section.removeEventListener('transitionend', handler);
    }, { once: true });

    // Gently scroll the row into view
    setTimeout(function () {
      row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 60);
  }

  function collapseAccordion(row, btn) {
    var section = row ? row.querySelector('.ib-accordion') : null;
    if (!section) return;

    if (btn) btn.textContent = 'Select Covers';
    row.classList.remove('ib-bundle--expanded');

    // Animate closed
    section.style.maxHeight = section.scrollHeight + 'px';
    section.getBoundingClientRect(); // force reflow
    section.style.transition = 'max-height 0.3s ease';
    section.style.maxHeight  = '0';

    section.addEventListener('transitionend', function handler() {
      section.remove();
      section.removeEventListener('transitionend', handler);
    }, { once: true });

    if (expandedRow === row) {
      expandedRow    = null;
      expandedBundle = null;
    }
  }

  function getArtistFromProduct(full) {
    var tags = full.tags || [];
    if (typeof tags === 'string') tags = tags.split(',').map(function (t) { return t.trim(); });
    for (var i = 0; i < tags.length; i++) {
      var lower = tags[i].toLowerCase();
      if (lower.indexOf('artist:') === 0) return tags[i].slice(7).trim();
      if (lower.indexOf('cover artist:') === 0) return tags[i].slice(13).trim();
    }
    return '';
  }

  function buildAccordionSection(bundle) {
    var section = document.createElement('div');
    section.className = 'ib-accordion';

    // ── Regular cards area (top) ──
    var cardsSection = document.createElement('div');
    cardsSection.className = 'ib-accordion__cards-section';

    var sectionLabel = document.createElement('div');
    sectionLabel.className = 'ib-accordion__section-label';
    var chooseText = (window.IB && window.IB.chooseText) || 'Choose your covers';
    var noteText   = (window.IB && window.IB.noteText)   || 'Note: Prices below reflect 20% off cover price for pre-ordering';
    sectionLabel.textContent = chooseText + ' \u2014 ' + noteText;

    var regularsEl = document.createElement('div');
    regularsEl.className = 'ib-accordion__regulars';
    regularsEl.innerHTML =
      '<div class="ib-accordion__inner-loading"><span class="ib-spinner"></span></div>';

    cardsSection.appendChild(sectionLabel);
    cardsSection.appendChild(regularsEl);
    section.appendChild(cardsSection);

    // ── Hover info bar (below cards) ──
    var hoverBar = document.createElement('div');
    hoverBar.className = 'ib-hover-bar ib-hover-bar--placeholder';
    hoverBar.innerHTML =
      '<span class="ib-hover-bar__label">Hovering:</span>' +
      '<div class="ib-hover-bar__content">' +
        '<div class="ib-hover-bar__title">Hover over a cover to see details</div>' +
        '<div class="ib-hover-bar__meta"></div>' +
      '</div>';
    section.appendChild(hoverBar);

    // ── Progress bar (below hover bar) ──
    var progressEl = document.createElement('div');
    progressEl.className = 'ib-accordion__progress';
    progressEl.innerHTML =
      '<div class="ib-progress__track"><div class="ib-progress__bar"></div></div>' +
      '<div class="ib-progress__meta">' +
        '<span class="ib-progress__label">0 of — copies selected</span>' +
        '<div class="ib-progress__thresholds"></div>' +
      '</div>';
    section.appendChild(progressEl);

    // ── Footer ──
    var footer = document.createElement('div');
    footer.className = 'ib-accordion__footer';

    var footerInfo = document.createElement('div');
    footerInfo.className = 'ib-accordion__footer-info';
    footerInfo.innerHTML =
      '<span class="ib-accordion__copy-count">0 copies</span>' +
      '<span class="ib-footer-dot" aria-hidden="true">·</span>' +
      '<span class="ib-accordion__subtotal">$0.00</span>';

    var atcBtn = document.createElement('button');
    atcBtn.className   = 'ib-accordion__atc';
    atcBtn.textContent = 'Add to Cart';
    atcBtn.disabled    = true;
    atcBtn.addEventListener('click', function () {
      addToCart(bundle, section, atcBtn);
    });

    footer.appendChild(footerInfo);
    footer.appendChild(atcBtn);
    section.appendChild(footer);

    updateProgress(bundle, section);

    // Render regular cards — instantly if cached, otherwise fetch
    var allCached = bundle.regulars.every(function (p) { return !!productCache[p.handle]; });
    if (allCached) {
      var fullProducts = bundle.regulars.map(function (p) { return productCache[p.handle]; });
      renderRegularCards(bundle, fullProducts, regularsEl, section);
    } else {
      Promise.all(bundle.regulars.map(function (p) {
        return fetchProductFull(p.handle).catch(function () { return null; });
      })).then(function (fullProducts) {
        renderRegularCards(bundle, fullProducts, regularsEl, section);
        // Re-measure accordion height after cards load (if transition already done)
        var accordion = regularsEl.closest('.ib-accordion');
        if (accordion && accordion.style.maxHeight === 'none') return; // already free
        if (accordion) accordion.style.maxHeight = accordion.scrollHeight + 'px';
      });
    }

    return section;
  }

  function renderRegularCards(bundle, fullProducts, regularsEl, section) {
    regularsEl.innerHTML = '';
    bundle.regulars.forEach(function (p, i) {
      var full = fullProducts[i];
      if (!full) return;

      var variant    = full.variants && full.variants[0];
      var variantId  = variant ? String(variant.id) : '';
      // /products/{handle}.js returns price in cents (399 = $3.99)
      var priceCents = variant ? Math.round(parseFloat(variant.price || 0)) : 0;
      var rawImg     = full.images && full.images[0];
      var imgSrc     = rawImg ? shopifyImg(rawImg.src || rawImg, 400) : '';

      var card = document.createElement('div');
      card.className = 'ib-card';

      var imgWrap = document.createElement('div');
      imgWrap.className = 'ib-card__img-wrap';

      var img = document.createElement('img');
      img.src     = imgSrc;
      img.alt     = full.title || '';
      img.loading = 'lazy';
      img.width   = 90;
      img.height  = 135;

      // Hover: update info bar on img mouseenter/mouseleave only (not whole card)
      var hoverTitle  = full.title || p.title || '';
      var hoverPrice  = fmtMoney(priceCents);
      var hoverArtist = getArtistFromProduct(full);
      img.addEventListener('mouseenter', function () {
        var bar = section.querySelector('.ib-hover-bar');
        if (!bar) return;
        bar.classList.remove('ib-hover-bar--placeholder');
        bar.querySelector('.ib-hover-bar__title').textContent = hoverTitle;
        bar.querySelector('.ib-hover-bar__meta').textContent =
          hoverArtist ? hoverArtist + ' · ' + hoverPrice : hoverPrice;
      });
      img.addEventListener('mouseleave', function () {
        var bar = section.querySelector('.ib-hover-bar');
        if (!bar) return;
        bar.classList.add('ib-hover-bar--placeholder');
        bar.querySelector('.ib-hover-bar__title').textContent = 'Hover over a cover to see details';
        bar.querySelector('.ib-hover-bar__meta').textContent = '';
      });

      imgWrap.appendChild(img);

      var priceEl = document.createElement('div');
      priceEl.className   = 'ib-card__price';
      priceEl.textContent = fmtMoney(priceCents);

      var qty = buildQtySelector(variantId, priceCents, bundle, section);

      var priceQtyRow = document.createElement('div');
      priceQtyRow.className = 'ib-card__price-qty';
      priceQtyRow.appendChild(priceEl);
      priceQtyRow.appendChild(qty);

      card.appendChild(imgWrap);
      card.appendChild(priceQtyRow);
      regularsEl.appendChild(card);
    });
    updateProgress(bundle, section);
  }

  function buildQtySelector(variantId, priceCents, bundle, section) {
    var wrap = document.createElement('div');
    wrap.className = 'ib-qty';

    var minus = document.createElement('button');
    minus.type        = 'button';
    minus.className   = 'ib-qty__btn';
    minus.textContent = '−';
    minus.setAttribute('aria-label', 'Decrease quantity');

    var input = document.createElement('input');
    input.type      = 'number';
    input.className = 'ib-qty__input';
    input.min       = '0';
    input.value     = String(cartQuantities[variantId] || 0);
    input.setAttribute('aria-label', 'Quantity');
    input.dataset.variantId  = variantId;
    input.dataset.priceCents = priceCents;

    var plus = document.createElement('button');
    plus.type        = 'button';
    plus.className   = 'ib-qty__btn';
    plus.textContent = '+';
    plus.setAttribute('aria-label', 'Increase quantity');

    function setQty(v) {
      v = Math.max(0, v);
      input.value = v;
      updateProgress(bundle, section);
    }

    minus.addEventListener('click', function () { setQty(parseInt(input.value) - 1); });
    plus.addEventListener('click',  function () { setQty(parseInt(input.value) + 1); });
    input.addEventListener('change', function () { setQty(parseInt(input.value) || 0); });

    wrap.appendChild(minus);
    wrap.appendChild(input);
    wrap.appendChild(plus);
    return wrap;
  }

  // ── Progress ─────────────────────────────────────────────────────────────────
  function updateProgress(bundle, section) {
    var progressBar    = section.querySelector('.ib-progress__bar');
    var progressLabel  = section.querySelector('.ib-progress__label');
    var thresholdPills = section.querySelector('.ib-progress__thresholds');
    var copyCountEl    = section.querySelector('.ib-accordion__copy-count');
    var subtotalEl     = section.querySelector('.ib-accordion__subtotal');
    var atcBtn         = section.querySelector('.ib-accordion__atc');
    var regularsEl     = section.querySelector('.ib-accordion__regulars');

    var totalQty   = 0;
    var totalCents = 0;
    if (regularsEl) {
      regularsEl.querySelectorAll('.ib-qty__input').forEach(function (inp) {
        var q = parseInt(inp.value) || 0;
        var c = parseInt(inp.dataset.priceCents) || 0;
        totalQty   += q;
        totalCents += q * c;
      });
    }

    var sortedThresholds = bundle.ratios.map(thresholdOf).sort(function (a, b) { return a - b; });
    var lowestT = sortedThresholds[0] || 1;

    if (progressBar) {
      var pct = Math.min(100, (totalQty / lowestT) * 100);
      progressBar.style.width = pct + '%';
      progressBar.classList.toggle('ib-progress__bar--met', totalQty >= lowestT);
    }
    if (progressLabel) {
      progressLabel.textContent =
        totalQty + ' of ' + lowestT + ' cop' + (lowestT === 1 ? 'y' : 'ies') + ' selected';
    }

    if (thresholdPills) {
      thresholdPills.innerHTML = '';
      bundle.ratios.forEach(function (ratio) {
        var t   = thresholdOf(ratio);
        var met = totalQty >= t;
        var pill = document.createElement('span');
        pill.className = 'ib-threshold-pill' + (met ? ' ib-threshold-pill--met' : '');
        pill.textContent = ratio + (met ? ' ✓' : '');
        thresholdPills.appendChild(pill);
      });
    }

    // Update compact listing overlays on incentive covers in the expanded row
    if (expandedRow) {
      expandedRow.querySelectorAll('.ib-bundle__incentive-wrap[data-threshold]').forEach(function (wrap) {
        var t         = parseInt(wrap.dataset.threshold) || 999;
        var isMet     = totalQty >= t;
        var overlay   = wrap.querySelector('.ib-bundle__inc-overlay');
        var lockLabel = wrap.querySelector('.ib-bundle__inc-lock-label');
        var imgWrapEl = wrap.querySelector('.ib-bundle__inc-img-wrap');
        var freeBadge = imgWrapEl ? imgWrapEl.querySelector('.ib-bundle__inc-free-badge') : null;

        var vidStr = wrap.dataset.variantId || '';
        var inCart = vidStr && (cartQuantities[vidStr] || 0) > 0;

        if (isMet) {
          if (!freeBadge && imgWrapEl) {
            var fb = document.createElement('span');
            fb.className   = 'ib-bundle__inc-free-badge';
            fb.textContent = 'FREE';
            imgWrapEl.appendChild(fb);
          }
          if (overlay) {
            overlay.classList.add('ib-bundle__inc-overlay--unlocked');
            overlay.classList.toggle('ib-bundle__inc-overlay--in-cart', !!inCart);
            if (lockLabel) lockLabel.textContent = inCart ? 'Already in cart' : '';
          }
        } else {
          if (freeBadge) freeBadge.remove();
          if (overlay) {
            overlay.classList.remove('ib-bundle__inc-overlay--unlocked');
            overlay.classList.remove('ib-bundle__inc-overlay--in-cart');
            if (lockLabel) lockLabel.textContent = 'Add ' + (t - totalQty) + ' more to unlock';
          }
        }
      });
    }

    if (copyCountEl) copyCountEl.textContent = totalQty + ' cop' + (totalQty === 1 ? 'y' : 'ies');
    if (subtotalEl)  subtotalEl.textContent  = fmtMoney(totalCents);
    if (atcBtn)      atcBtn.disabled         = totalQty === 0;
  }

  // ── Cart sync ─────────────────────────────────────────────────────────────────
  function onCartUpdate() {
    fetchJSON('/cart.js').then(function (cartData) {
      cartQuantities = {};
      if (cartData && Array.isArray(cartData.items)) {
        cartData.items.forEach(function (item) {
          var vid = String(item.variant_id);
          cartQuantities[vid] = (cartQuantities[vid] || 0) + item.quantity;
        });
      }
      syncAccordionToCart();
    }).catch(function () {});
  }

  function syncAccordionToCart() {
    if (!expandedRow || !expandedBundle) return;
    var section = expandedRow.querySelector('.ib-accordion');
    if (!section) return;
    var regularsEl = section.querySelector('.ib-accordion__regulars');
    if (regularsEl) {
      regularsEl.querySelectorAll('.ib-qty__input').forEach(function (inp) {
        var vidStr = inp.dataset.variantId || '';
        inp.value = String(cartQuantities[vidStr] || 0);
      });
    }
    updateProgress(expandedBundle, section);
  }

  // ── Add to cart ───────────────────────────────────────────────────────────────
  async function addToCart(bundle, section, atcBtn) {
    atcBtn.disabled    = true;
    atcBtn.textContent = 'Adding…';

    var items      = [];
    var totalQty   = 0;
    var regularsEl = section.querySelector('.ib-accordion__regulars');

    if (regularsEl) {
      regularsEl.querySelectorAll('.ib-qty__input').forEach(function (inp) {
        var selectorQty = parseInt(inp.value) || 0;
        var id          = parseInt(inp.dataset.variantId);
        var vidStr      = inp.dataset.variantId || '';
        var alreadyIn   = cartQuantities[vidStr] || 0;
        var delta       = selectorQty - alreadyIn;
        console.log('[IB] ATC regular: variantId=' + vidStr + ' parsed=' + id + ' selectorQty=' + selectorQty + ' alreadyIn=' + alreadyIn + ' delta=' + delta);
        totalQty += selectorQty; // full value used for incentive threshold check
        if (delta > 0 && id) {
          items.push({ id: id, quantity: delta });
        }
      });
    }

    console.log('[IB] ATC: regular items=' + JSON.stringify(items) + ' totalQty=' + totalQty);

    // Collect qualifying incentive items from bundle data (no drawer panel)
    bundle.incentives.forEach(function (inc) {
      var ratio  = getRatioFromTags(inc.tags);
      var t      = thresholdOf(ratio);
      var variant = inc.variants && inc.variants[0];
      var vidRaw  = variant ? String(variant.id) : '';
      var vid     = parseInt(vidRaw.indexOf('/') !== -1 ? vidRaw.split('/').pop() : vidRaw);
      var met           = totalQty >= t;
      var vidStr        = String(vid);
      var alreadyInCart = met && (cartQuantities[vidStr] || 0) > 0;
      console.log('[IB] ATC incentive: threshold=' + t + ' vidRaw=' + vidRaw + ' vid=' + vid + ' totalQty=' + totalQty + ' met=' + met + ' alreadyInCart=' + alreadyInCart);
      if (met && vid && !alreadyInCart) {
        items.push({ id: vid, quantity: 1, properties: {
          '_reward':   'incentive-free',
          'Free with': t + ' copies of ' + (bundle.seriesTitle || 'this series') + ' (requirement must be met to qualify)',
        }});
      }
    });

    console.log('[IB] ATC: posting ' + items.length + ' item(s) to /cart/add.js');

    if (!items.length) {
      console.log('[IB] ATC: no items, aborting');
      atcBtn.disabled    = false;
      atcBtn.textContent = 'Add to Cart';
      return;
    }

    try {
      var cartDrawer = document.querySelector('cart-drawer');

      // product-form.js uses fetchConfig('javascript') which sets Accept: application/javascript.
      // Shopify only returns inline section HTML when this Accept header is present.
      // We keep Content-Type: application/json so we can send the items array.
      var sectionsToRender = (cartDrawer && cartDrawer.getSectionsToRender)
        ? cartDrawer.getSectionsToRender().map(function (s) { return s.id; })
        : ['cart-drawer', 'cart-icon-bubble'];

      console.log('[IB] ATC: posting ' + items.length + ' item(s), sections=' + JSON.stringify(sectionsToRender));

      var r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
        },
        body: JSON.stringify({
          items: items,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        }),
      });

      var responseBody = await r.json().catch(function (e) {
        console.error('[IB] ATC: failed to parse response JSON', e);
        return null;
      });

      console.log('[IB] ATC: /cart/add.js status=' + r.status + ' ok=' + r.ok);
      console.log('[IB] ATC: responseBody keys=' + (responseBody ? Object.keys(responseBody).join(',') : 'null'));
      console.log('[IB] ATC: responseBody.sections=' + (responseBody && responseBody.sections ? JSON.stringify(Object.keys(responseBody.sections)) : 'missing'));

      if (!r.ok) {
        throw new Error((responseBody && responseBody.description) || ('Cart error ' + r.status));
      }

      // Update local cart state with newly added items
      items.forEach(function (item) {
        var vid = String(item.id);
        cartQuantities[vid] = (cartQuantities[vid] || 0) + item.quantity;
      });

      if (cartDrawer && responseBody && responseBody.sections) {
        console.log('[IB] ATC: updating cart drawer DOM manually');

        // Replicate what renderContents does but skip its internal open() call,
        // which triggers trapFocus on #CartDrawer and fails on this page.
        var parser = new DOMParser();

        // Update #CartDrawer innerHTML (mirrors getSectionsToRender cart-drawer entry)
        var drawerSectionHtml = responseBody.sections['cart-drawer'];
        if (drawerSectionHtml) {
          var drawerDoc    = parser.parseFromString(drawerSectionHtml, 'text/html');
          var drawerSource = drawerDoc.querySelector('#CartDrawer');
          var drawerTarget = document.querySelector('#CartDrawer');
          if (drawerTarget && drawerSource) {
            drawerTarget.innerHTML = drawerSource.innerHTML;
            console.log('[IB] ATC: #CartDrawer updated');
          }
        }

        // Update cart-icon-bubble innerHTML (mirrors getSectionsToRender bubble entry)
        var bubbleSectionHtml = responseBody.sections['cart-icon-bubble'];
        if (bubbleSectionHtml) {
          var bubbleDoc    = parser.parseFromString(bubbleSectionHtml, 'text/html');
          var bubbleSource = bubbleDoc.querySelector('.shopify-section');
          var bubbleTarget = document.getElementById('cart-icon-bubble');
          if (bubbleTarget && bubbleSource) {
            bubbleTarget.innerHTML = bubbleSource.innerHTML;
            console.log('[IB] ATC: cart-icon-bubble updated');
          }
        }

        // Remove is-empty so the drawer shows items, not the empty state
        var drawerInner = cartDrawer.querySelector('.drawer__inner');
        if (drawerInner) drawerInner.classList.remove('is-empty');
        cartDrawer.classList.remove('is-empty');

        // Open the drawer; trapFocus can fail on this page so wrap it
        try {
          cartDrawer.open();
        } catch (openErr) {
          console.warn('[IB] ATC: cartDrawer.open() threw (likely trapFocus) — adding classes directly', openErr);
          cartDrawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
        }
      } else {
        console.warn('[IB] ATC: no sections in response — opening drawer without DOM update');
        if (cartDrawer) {
          try { cartDrawer.open(); } catch (e) {
            cartDrawer.classList.add('animate', 'active');
            document.body.classList.add('overflow-hidden');
          }
        }
      }

      // Publish cartUpdate so CartDrawerItems and other subscribers stay in sync
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
      }

      console.log('[IB] ATC: done');
      atcBtn.disabled    = false;
      atcBtn.textContent = 'Add to Cart';
    } catch (e) {
      console.error('[IncentiveBundles] cart error', e);
      alert('Could not add to cart: ' + e.message);
      atcBtn.disabled    = false;
      atcBtn.textContent = 'Add to Cart';
    }
  }

  // ── UI helpers ───────────────────────────────────────────────────────────────
  function showState(state) {
    el.loading.style.display = state === 'loading' ? '' : 'none';
    el.error.style.display   = state === 'error'   ? '' : 'none';
    el.empty.style.display   = state === 'empty'   ? '' : 'none';
    el.bundles.style.display = state === 'bundles' ? '' : 'none';
  }

  // ── Data helpers ─────────────────────────────────────────────────────────────
  function getSidTag(tags) {
    for (var i = 0; i < tags.length; i++) {
      if (tags[i].indexOf('sid-') === 0) return tags[i];
    }
    return null;
  }

  function getRatioFromTags(tags) {
    for (var i = 0; i < tags.length; i++) {
      var m = tags[i].match(/^ratio-(.+)$/);
      if (m) return m[1];
    }
    return '';
  }

  function extractRatios(incentives) {
    var seen = {}, ratios = [];
    incentives.forEach(function (p) {
      var r = getRatioFromTags(p.tags);
      if (r && !seen[r]) { seen[r] = true; ratios.push(r); }
    });
    return ratios.sort(function (a, b) { return thresholdOf(a) - thresholdOf(b); });
  }

  function thresholdOf(ratio) {
    if (!ratio) return 999;
    var parts = ratio.split(':');
    return parseInt(parts[parts.length - 1]) || 999;
  }

  function extractSeriesTitle(title) {
    var issueMatch = (title || '').match(/^(.+?#\s*\d+)/);
    if (issueMatch) return issueMatch[1].trim();
    return (title || '')
      .replace(/\s*\d+:\d+\s*(incentive|variant).*/i, '')
      .replace(/\s+(incentive|variant\s*[a-z]?)\s*$/i, '')
      .replace(/\s+cover\s+[a-z\d]\s*$/i, '')
      .trim();
  }

  function getProductImageSrc(product, width) {
    var src = '';
    if (product.images && product.images.length) {
      src = product.images[0].src || product.images[0].url || '';
    } else if (product.image) {
      src = typeof product.image === 'string' ? product.image
          : (product.image.url || product.image.src || '');
    } else if (product.featured_image) {
      src = typeof product.featured_image === 'string' ? product.featured_image
          : (product.featured_image.url || product.featured_image.src || '');
    }
    return shopifyImg(src, width);
  }

  function shopifyImg(src, w) {
    if (!src) return '';
    return src.replace(/(\.(jpg|jpeg|png|gif|webp))(\?.*)?$/i,
      function (_, ext, __, q) { return '_' + w + 'x' + ext + (q || ''); });
  }

  function fmtMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ── Boot ─────────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
